import {Component, OnInit, OnChanges, OnDestroy} from '@angular/core';
import {CnImportApiService} from '../../../service/api/cn-import-api/cn-import-api.service';
import {CnAgentPromptDto} from '../../../model/dto/api/cn-agent-prompt.dto';
import {
    CommissionImportUploadMethodEnum,
    CommissionImportUploadMethodLabels
} from '../../../model/protected-domain/enum/commission-import-upload-method/commission-import-upload-method.enum';
import {cnErrorMessage} from '../../../shared/cn-error-message.util';

/**
 * Provisions-Import start component (P3), hosted by CnImportPage.
 *
 * Lets a Sachbearbeiter:
 *  1. pick a Gesellschaft (cn.CommissionPartner, by NAME) from CnImportApiService.loadPartnerOptions(),
 *  2. choose an import method (CommissionImportUploadMethodEnum: Lokaler KI-Agent / Strukturierte Datei /
 *     Manuell; default LOCAL_AI_AGENT),
 *  3. for LOCAL_AI_AGENT — generate a copyable prompt for their local AI agent via
 *     CnImportApiService.getAgentPrompt(commissionPartnerId).
 *
 * NO business logic runs in the FE — the prompt, the import-profile metadata and the
 * profileConfigured flag all come from the backend. STRUCTURED_FILE / MANUAL are placeholders for now.
 *
 * UI states (FR-16) for the prompt workflow: idle / busy / ready / error.
 */
@Component({
    selector: 'mvs-cn-import-start',
    templateUrl: './cn-import-start.component.html',
    styleUrls: ['./cn-import-start.component.scss'],
    standalone: false
})
export class CnImportStartComponent implements OnInit, OnChanges, OnDestroy {

    /** Expose the enum to the template so it can reference the method values by name. */
    readonly UploadMethod = CommissionImportUploadMethodEnum;

    // ===== Partner (Gesellschaft) selection =====
    /**
     * Gesellschaft options for the partner dropdown: selectable by NAME (value = partner id).
     * Loaded from the CONFIGURED commission partners only (cn.CommissionPartner → cm.Partner).
     */
    partnerOptions: { label: string; value: number }[] = [];
    /** Selected Gesellschaft (commission partner) id, or null when none chosen. */
    selectedPartnerId: number | null = null;
    /** Loading state for the partner options (skeleton vs. ready dropdown). */
    partnerOptionsLoading: boolean = false;

    // ===== Import-method selection =====
    /**
     * Import-method options (the 3 CommissionImportUploadMethodEnum values with German labels).
     * value is the enum ordinal; label is the German display text.
     */
    readonly uploadMethodOptions: { label: string; value: CommissionImportUploadMethodEnum }[] = [
        {
            label: CommissionImportUploadMethodLabels[CommissionImportUploadMethodEnum.LOCAL_AI_AGENT],
            value: CommissionImportUploadMethodEnum.LOCAL_AI_AGENT
        },
        {
            label: CommissionImportUploadMethodLabels[CommissionImportUploadMethodEnum.STRUCTURED_FILE],
            value: CommissionImportUploadMethodEnum.STRUCTURED_FILE
        },
        {
            label: CommissionImportUploadMethodLabels[CommissionImportUploadMethodEnum.MANUAL],
            value: CommissionImportUploadMethodEnum.MANUAL
        }
    ];
    /** Selected import method; defaults to LOCAL_AI_AGENT (the primary, AI-driven path). */
    selectedUploadMethod: CommissionImportUploadMethodEnum = CommissionImportUploadMethodEnum.LOCAL_AI_AGENT;

    // ===== Agent-prompt workflow =====
    promptState: 'idle' | 'busy' | 'ready' | 'error' = 'idle';
    promptError: string | null = null;
    /** The loaded agent-prompt result (metadata + rendered prompt) from the backend. */
    promptResult: CnAgentPromptDto | null = null;
    /** Short-lived flag that shows the 'Kopiert!' confirmation after a clipboard copy. */
    copyConfirmed: boolean = false;
    /** Handle for the copy-confirmation auto-reset timer, cleared on the next copy. */
    private copyConfirmTimer: ReturnType<typeof setTimeout> | null = null;

    initialized: boolean = false;

    constructor(
        private importApiService: CnImportApiService
    ) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * React to bound input changes. The component is currently self-contained (no @Input
     * bindings); the hook is implemented to honour the module lifecycle convention and to keep
     * the contract for any future page-supplied context inputs.
     */
    ngOnChanges(): void {
    }

    /**
     * Clean up on destroy: clear the pending 'Kopiert!' confirmation auto-reset timer so the
     * setTimeout handle cannot fire (and leak) after the component is gone.
     */
    ngOnDestroy(): void {
        this.clearCopyConfirmation();
    }

    /**
     * Initialize the component: load the selectable Gesellschaft list so the user picks by NAME.
     */
    initComponent(): void {
        this.loadPartnerOptions();
        this.initialized = true;
    }

    /**
     * AI: Load the selectable Gesellschaft list for the partner dropdown.
     * Reuses the canonical partner-dropdown query from CnImportApiService (no FE query duplication);
     * maps {id, name} to the {label, value} shape the p-select consumes.
     */
    loadPartnerOptions(): void {
        this.partnerOptionsLoading = true;
        this.importApiService.loadPartnerOptions().subscribe({
            next: (rows) => {
                this.partnerOptions = (rows ?? []).map(r => ({label: r.name, value: r.id}));
                this.partnerOptionsLoading = false;
            },
            error: () => {
                // Non-fatal: the dropdown stays empty and the user sees the empty-state hint.
                this.partnerOptions = [];
                this.partnerOptionsLoading = false;
            }
        });
    }

    /** Whether the chosen method is the local AI agent path. */
    isLocalAiAgent(): boolean {
        return this.selectedUploadMethod === CommissionImportUploadMethodEnum.LOCAL_AI_AGENT;
    }

    /**
     * Whether the chosen method is one of the not-yet-implemented paths (file / manual) — i.e.
     * any method other than the local AI agent (the enum has exactly these three values).
     */
    isPlaceholderMethod(): boolean {
        return this.selectedUploadMethod !== CommissionImportUploadMethodEnum.LOCAL_AI_AGENT;
    }

    /**
     * Reset the prompt workflow when the user changes the Gesellschaft or the import method —
     * a previously generated prompt belongs to the old selection and must not linger.
     */
    onSelectionChanged(): void {
        this.promptState = 'idle';
        this.promptError = null;
        this.promptResult = null;
        this.clearCopyConfirmation();
    }

    /** Whether the "Prompt generieren" action can be triggered. */
    canGeneratePrompt(): boolean {
        return this.isLocalAiAgent()
            && this.selectedPartnerId != null
            && this.promptState !== 'busy';
    }

    /**
     * Load the rendered AI agent prompt + import-profile metadata for the selected Gesellschaft
     * (GET /cn/import-profile/agent-prompt). The prompt is shown read-only and is copyable; when
     * profileConfigured is false the prompt still renders (it contains sensible defaults).
     */
    generatePrompt(): void {
        if (!this.canGeneratePrompt()) {
            return;
        }

        this.promptState = 'busy';
        this.promptError = null;
        this.promptResult = null;
        this.clearCopyConfirmation();

        this.importApiService.getAgentPrompt(this.selectedPartnerId!).subscribe({
            next: (value) => {
                this.promptResult = value ?? null;
                this.promptState = 'ready';
            },
            error: (err) => {
                this.promptState = 'error';
                this.promptError = cnErrorMessage(err);
            }
        });
    }

    /**
     * Copy the rendered prompt to the clipboard and show a brief "Kopiert!" confirmation.
     * Falls back to an error message when the Clipboard API is unavailable or denied.
     */
    copyPrompt(): void {
        const prompt = this.promptResult?.prompt;
        if (!prompt) {
            return;
        }
        if (!navigator.clipboard?.writeText) {
            this.promptError = 'Kopieren in die Zwischenablage wird von diesem Browser nicht unterstützt.';
            return;
        }
        navigator.clipboard.writeText(prompt).then(
            () => this.showCopyConfirmation(),
            () => {
                this.promptError = 'Der Prompt konnte nicht in die Zwischenablage kopiert werden.';
            }
        );
    }

    /** Show the "Kopiert!" confirmation for a short moment, then auto-reset it. */
    private showCopyConfirmation(): void {
        this.clearCopyConfirmation();
        this.copyConfirmed = true;
        this.copyConfirmTimer = setTimeout(() => {
            this.copyConfirmed = false;
            this.copyConfirmTimer = null;
        }, 2000);
    }

    /** Clear the copy confirmation flag and any pending auto-reset timer. */
    private clearCopyConfirmation(): void {
        this.copyConfirmed = false;
        if (this.copyConfirmTimer != null) {
            clearTimeout(this.copyConfirmTimer);
            this.copyConfirmTimer = null;
        }
    }
}
